import type { SelectedFilters } from '@/hooks/filters/useCompanyAttributesFilters';
import type {
    CompensationAudience,
    CompensationAudienceAttribute,
    CompensationAudienceFilter,
} from '@/types/compensation';

type FilterAttribute = { id?: string };

const toIds = (items: FilterAttribute[]): string[] =>
    items.map((item) => item.id).filter((id): id is string => !!id);

/** The three `SelectedFilters` buckets a `SpecificTeams` audience can filter on, paired with the
 * `CompensationAudienceFilter.attribute` each maps to. Every other bucket of `SelectedFilters`
 * (employee levels, managers, custom attributes) has no equivalent on `CompensationAudience`: the
 * envelope's audience picker only ever renders `CompanyAttributeFilters attributes={[...]}` with
 * these three, so nothing else can be selected in the first place. */
const AUDIENCE_FILTER_KEYS: readonly [keyof SelectedFilters, CompensationAudienceAttribute][] = [
    ['departments', 'Department'],
    ['jobTitles', 'JobTitle'],
    ['companyAddresses', 'CompanyAddress'],
];

/** The company attribute families an envelope's audience may be filtered on. */
export const AUDIENCE_ATTRIBUTE_FAMILIES: readonly string[] = AUDIENCE_FILTER_KEYS.map(
    ([filterKey]) => String(filterKey),
);

/**
 * Converts the audience picker's `SelectedFilters` into the `CompensationAudienceFilter[]` a
 * draft/save request sends: ids only, one filter per non-empty bucket. A bucket with nothing
 * selected is omitted entirely rather than sent as an empty `valueIds`, matching how every other
 * array filter on this contract means "no restriction" only when the filter itself is absent.
 */
export const toCompensationAudienceFilters = (
    filters: SelectedFilters,
): CompensationAudienceFilter[] =>
    AUDIENCE_FILTER_KEYS.map(([filterKey, attribute]) => ({
        attribute,
        valueIds: toIds(filters[filterKey] as FilterAttribute[]),
    })).filter((filter) => filter.valueIds.length > 0);

/**
 * Whether a "Équipes spécifiques" audience still names no value at all. The backend resolves such
 * an audience to nobody — never to the whole company — so the screen has to say why the envelope
 * is empty instead of blaming the cycle's evaluations.
 */
export const isMissingAudienceCriterion = (audience: CompensationAudience): boolean =>
    audience.type === 'SpecificTeams' &&
    audience.filters.every((filter) => filter.valueIds.length === 0);

/** Ids only, grouped the way `SelectedFilters` groups them; the caller resolves them against the
 * company's attribute list. */
export type CompensationAudienceFilterIds = {
    departments: string[];
    jobTitles: string[];
    companyAddresses: string[];
};

const EMPTY_AUDIENCE_FILTER_IDS: CompensationAudienceFilterIds = {
    departments: [],
    jobTitles: [],
    companyAddresses: [],
};

/**
 * The reverse of `toCompensationAudienceFilters`: reads a saved envelope's audience filters back
 * into id buckets keyed the same way `SelectedFilters` is, so a caller with the company's
 * attribute list can re-select the matching entries when opening an existing `SpecificTeams`
 * envelope.
 */
export const fromCompensationAudienceFilters = (
    filters: CompensationAudienceFilter[],
): CompensationAudienceFilterIds =>
    AUDIENCE_FILTER_KEYS.reduce((result, [filterKey, attribute]) => {
        const match = filters.find((filter) => filter.attribute === attribute);
        return { ...result, [filterKey]: match ? [...match.valueIds] : [] };
    }, EMPTY_AUDIENCE_FILTER_IDS);
